
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { CreditCard, Plus, History, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { BonusCreditGrant } from './BonusCreditGrant';
import { creditsEvents } from '@/utils/creditsEvents';

interface CreditSummary {
  daily_bonus: number;
  monthly_base: number;
  rollover: number;
  bonus: number;
  stored: number;
  total: number;
  low_credit_warning: boolean;
  current_cycle?: {
    cycle_start_date: string;
    cycle_end_date: string;
    billing_anchor_day: number;
  };
}

interface CreditTransaction {
  id: string;
  credit_type: string;
  amount: number;
  balance_after: number;
  transaction_type: string;
  description: string;
  reason?: string;
  created_at: string;
  metadata?: any;
}

interface CreditManagementPanelProps {
  userId: string;
  userEmail: string | null;
}

export function CreditManagementPanel({ userId, userEmail }: CreditManagementPanelProps) {
  const [credits, setCredits] = useState<CreditSummary | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  
  // Adjustment form state
  const [creditType, setCreditType] = useState<string>('stored');
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [adjusting, setAdjusting] = useState(false);

  const { toast } = useToast();

  useEffect(() => {
    if (userId) {
      fetchUserCredits();
      fetchTransactionHistory();
    }
  }, [userId]);

  const fetchUserCredits = async () => {
    try {
      // Temporarily authenticate as the target user to get their credits
      const { data, error } = await supabase.functions.invoke('get-credits', {
        headers: {
          'x-target-user-id': userId // Custom header for admin access
        }
      });
      
      if (error) {
        console.error('Error fetching user credits:', error);
        return;
      }

      setCredits(data);
    } catch (error) {
      console.error('Error fetching user credits:', error);
    }
  };

  const fetchTransactionHistory = async () => {
    try {
      setLoading(true);
      // Fetch recent transactions for this user
      const { data, error } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Error fetching transactions:', error);
        return;
      }

      setTransactions(data || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustCredits = async () => {
    if (!amount || isNaN(Number(amount))) {
      toast({
        title: "Invalid Amount",
        description: "Please enter a valid number",
        variant: "destructive",
      });
      return;
    }

    try {
      setAdjusting(true);
      
      const { data, error } = await supabase.functions.invoke('adjust-credits', {
        body: {
          user_id: userId,
          credit_type: creditType,
          amount: Number(amount),
          description: description || undefined,
          admin_notes: adminNotes || undefined
        }
      });

      if (error) {
        throw error;
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to adjust credits');
      }

      toast({
        title: "Credits Adjusted",
        description: data.message,
      });

      // Reset form and refresh data
      setAmount('');
      setDescription('');
      setAdminNotes('');
      setAdjustmentDialogOpen(false);
      
      await fetchUserCredits();
      await fetchTransactionHistory();

      // Emit event to update other components
      creditsEvents.emit({ userId, action: 'adjust' });

    } catch (error: any) {
      console.error('Error adjusting credits:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to adjust credits",
        variant: "destructive",
      });
    } finally {
      setAdjusting(false);
    }
  };

  const handleBonusGrantComplete = async () => {
    await fetchUserCredits();
    await fetchTransactionHistory();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getCreditTypeLabel = (type: string) => {
    switch (type) {
      case 'daily_bonus': return 'Daily Bonus';
      case 'monthly_base': return 'Monthly Base';
      case 'rollover': return 'Rollover';
      case 'bonus': return 'Bonus';
      case 'stored': return 'Purchased';
      default: return type;
    }
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'grant': return 'bg-green-100 text-green-800';
      case 'consume': return 'bg-red-100 text-red-800';
      case 'expire': return 'bg-gray-100 text-gray-800';
      case 'rollover': return 'bg-blue-100 text-blue-800';
      case 'purchase': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTransactionSource = (tx: CreditTransaction) => {
    if (tx.reason === 'bonus_license') return '(License)';
    if (tx.reason === 'bonus_admin') return '(Admin)';
    return '';
  };

  if (loading && !credits) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Credit Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-8">
            Loading credit information...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Credit Management
          </CardTitle>
          <CardDescription>
            Manage credits for {userEmail}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {credits ? (
            <>
              {/* Credit Balance Summary */}
              <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                <div className="text-center space-y-1">
                  <div className="text-2xl font-bold text-primary">
                    {(credits.total || 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Total</div>
                </div>
                <div className="text-center space-y-1">
                  <div className="text-lg font-semibold text-green-600">
                    {credits.daily_bonus || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Daily</div>
                </div>
                <div className="text-center space-y-1">
                  <div className="text-lg font-semibold text-blue-600">
                    {credits.monthly_base || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Monthly</div>
                </div>
                <div className="text-center space-y-1">
                  <div className="text-lg font-semibold text-yellow-600">
                    {credits.rollover || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Rollover</div>
                </div>
                <div className="text-center space-y-1">
                  <div className="text-lg font-semibold text-orange-600">
                    {credits.bonus || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Bonus</div>
                </div>
                <div className="text-center space-y-1">
                  <div className="text-lg font-semibold text-purple-600">
                    {credits.stored || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Purchased</div>
                </div>
              </div>

              {credits.low_credit_warning && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                  <span className="text-sm text-destructive">
                    Low credit warning: User has {credits.total} credits remaining
                  </span>
                </div>
              )}

              <Separator />

              {/* Admin Actions */}
              <div className="flex gap-2">
                <Dialog open={adjustmentDialogOpen} onOpenChange={setAdjustmentDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Adjust Credits
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Adjust Credits</DialogTitle>
                      <DialogDescription>
                        Add or remove credits for {userEmail}
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Credit Type</Label>
                        <Select value={creditType} onValueChange={setCreditType}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily_bonus">Daily Bonus</SelectItem>
                            <SelectItem value="monthly_base">Monthly Base</SelectItem>
                            <SelectItem value="rollover">Rollover</SelectItem>
                            <SelectItem value="bonus">Bonus</SelectItem>
                            <SelectItem value="stored">Purchased/Stored</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Amount</Label>
                        <Input
                          type="number"
                          placeholder="Enter amount (positive to add, negative to subtract)"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Input
                          placeholder="Reason for adjustment"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Admin Notes</Label>
                        <Textarea
                          placeholder="Internal notes (not visible to user)"
                          value={adminNotes}
                          onChange={(e) => setAdminNotes(e.target.value)}
                        />
                      </div>
                    </div>

                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setAdjustmentDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleAdjustCredits}
                        disabled={adjusting || !amount}
                      >
                        {adjusting ? 'Adjusting...' : 'Adjust Credits'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <History className="h-4 w-4 mr-2" />
                      Transaction History
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl">
                    <DialogHeader>
                      <DialogTitle>Credit Transaction History</DialogTitle>
                      <DialogDescription>
                        Recent credit transactions for {userEmail}
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="max-h-96 overflow-y-auto">
                      {transactions.length > 0 ? (
                        <div className="space-y-2">
                          {transactions.map((tx) => (
                            <div key={tx.id} className="flex items-center justify-between p-3 border rounded-lg">
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary" className={getTransactionTypeColor(tx.transaction_type)}>
                                    {tx.transaction_type}
                                  </Badge>
                                  <span className="text-sm font-medium">
                                    {getCreditTypeLabel(tx.credit_type)}
                                  </span>
                                  <span className={`text-sm font-semibold ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {tx.amount > 0 ? '+' : ''}{tx.amount}
                                  </span>
                                  {tx.reason && (
                                    <Badge variant="outline" className="text-xs">
                                      {getTransactionSource(tx)}
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {tx.description}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {formatDate(tx.created_at)} • Balance after: {tx.balance_after}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center text-muted-foreground py-8">
                          No transactions found
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </>
          ) : (
            <div className="text-center text-muted-foreground py-8">
              Credit system is disabled or user data not available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bonus Credit Grant Component */}
      {credits && (
        <BonusCreditGrant 
          userId={userId} 
          userEmail={userEmail} 
          onGrantComplete={handleBonusGrantComplete} 
        />
      )}
    </div>
  );
}
